import { useState } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import {
  useNavigate,
} from 'react-router';
import {
  get,
} from 'lodash';
import {
  API,
  NavigationHelper,
  GlobalActions,
  ToastActions,
  SidePanelContainer,
  SidePanelHeader,
  SidePanelContent,
  SidePanelFooter,
  SidePanelSection,
  VibeModal,
  VibeButton,
  VibeTooltip,
  VibeIcon,
  viClose,
  viArchive,
  viUnarchive,
  color,
} from 'vibeguide';
import Information from './Information';
import Domains from './Domains';

function IdentityProviderDetails({
  className = '',
  style = {},
  identityProvider = {},
  isNew = false,
  user,
  setPanel,
  queueToast,
  onUpdate = () => {},
}) {
  const [confirmArchive, setConfirmArchive] = useState(false);
  const history = useNavigate();

  const onClose = () => {
    setPanel({
      show: false,
    });
  };

  /**
   * Archive the item
   */
  const onClickArchive = () => {
    setConfirmArchive(true);
  };

  /**
   * When the archive modal is closed
   */
  const onCloseArchive = () => {
    setConfirmArchive(false);
  };

  /**
   * Unarchive the item
   */
  const onClickUnarchive = async () => {
    await API.IdentityProvider.reactivate({
      _id: identityProvider._id,
    });

    onClose();

    // tell listening components the object was saved
    document.dispatchEvent(new Event('onSaveIdentityProvider'));
  };

  /**
   * When the archive modal is confirmed
   */
  const onConfirmArchive = async () => {
    await API.IdentityProvider.deactivate(identityProvider._id);

    onCloseArchive();
    onClose();

    // tell listening components the object was saved
    document.dispatchEvent(new Event('onSaveIdentityProvider'));
  };

  const onSave = async () => {
    const data = {
      name: identityProvider.name,
      domains: (identityProvider.domains || []).filter(domain => domain && domain.trim()),
      cognitoAppClientId: identityProvider.cognitoAppClientId,
      cognitoProviderName: identityProvider.cognitoProviderName,
    };

    if (identityProvider._id) {
      // editing an identity provider
      data._id = identityProvider._id;
    }

    queueToast({
      type: 'info',
      title: 'Saving...',
      allowClose: true,
    });

    try {
      const response = identityProvider._id
        ? await API.IdentityProvider.update(data)
        : await API.IdentityProvider.create(data);

      const identityProviderId = get(response, '[0].documentId', null);
      const responseType = get(response, '[0].type');

      const success = data._id
        ? responseType === 'IDENTITYPROVIDER.UPDATED'
        : responseType === 'IDENTITYPROVIDER.CREATED';

      if (success) {
        // Successfully saved the identity provider
        const saveMessage = data._id
          ? 'Identity Provider Updated!'
          : 'Identity Provider Created!';

        queueToast({
          type: 'success',
          title: saveMessage,
          allowClose: true,
        });

        // tell listening components the object was saved
        document.dispatchEvent(new Event('onSaveIdentityProvider'));

        // creating a new object, redirect to the proper URL
        if (!identityProvider._id) {
          const redirectUrl = NavigationHelper.updateParams({
            identityProviderId,
            type: null,
          });

          history(redirectUrl);
        }
      } else {
        console.error('Error saving identity provider to API', response);

        queueToast({
          type: 'error',
          title: 'Error Saving Identity Provider',
          timeout: 10,
          allowClose: true,
        });
      }
    } catch (err) {
      console.error('Error saving identity provider', err);

      queueToast({
        type: 'error',
        title: 'Error Saving Identity Provider',
        timeout: 10,
        allowClose: true,
      });
    }
  };

  const validDomains = (identityProvider.domains || []).filter(domain => domain && domain.trim());

  const disableSave = !identityProvider.name
    || validDomains.length <= 0
    || !identityProvider.cognitoAppClientId
    || !identityProvider.cognitoProviderName;

  const disableInput = (identityProvider._id && !user.can('identity_provider.modify'))
    || (!identityProvider._id && !user.can('identity_provider.create'));

  const showArchive = user.can('identity_provider.delete');
  const isArchived = !isNew && identityProvider._id && !identityProvider.active;

  return (
    <SidePanelContainer
      className={classNames('IdentityProviderDetails', className)}
      style={style}
    >
      <SidePanelHeader
        icons={(
          <VibeIcon
            className="close"
            icon={viClose}
            color={color.manatee}
            hoverColor={color.obsidian}
            size={24}
            onClick={onClose}
            data-testid="identity-provider-close-button"
          />
        )}
      >
        <div className="title">
          <VibeTooltip title={identityProvider.name || 'New Identity Provider'}>
            <span>
              {identityProvider._id
                ? identityProvider.name
                : 'New Identity Provider'}
            </span>
          </VibeTooltip>
        </div>
      </SidePanelHeader>

      <SidePanelContent>
        <SidePanelSection title="Information">
          <Information
            name={identityProvider.name}
            cognitoAppClientId={identityProvider.cognitoAppClientId}
            cognitoProviderName={identityProvider.cognitoProviderName}
            disableInput={disableInput}
            onUpdate={onUpdate}
          />
        </SidePanelSection>

        <SidePanelSection title="Domains">
          <Domains
            domains={identityProvider.domains}
            disableInput={disableInput}
            onUpdate={onUpdate}
          />
        </SidePanelSection>
      </SidePanelContent>

      <SidePanelFooter className="panel-footer">
        <VibeButton
          text="Save Identity Provider"
          style={{
            marginRight: 12,
          }}
          color="secondary"
          disabled={disableSave || disableInput}
          testId="identity-provider-save-button"
          onClick={onSave}
        />

        {identityProvider._id && (
          <div className="toolbar-buttons">
            {showArchive && !isArchived ? (
              <div className="toolbar-button">
                <VibeIcon
                  icon={viArchive}
                  type="button"
                  buttonProps={{
                    size: 32,
                    borderColor: color.error,
                  }}
                  tooltip="Archive"
                  color={color.error}
                  size={20}
                  onClick={onClickArchive}
                  data-testid="identity-provider-archive-button"
                />
              </div>
            ) : null}

            {showArchive && isArchived ? (
              <div className="toolbar-button">
                <VibeIcon
                  icon={viUnarchive}
                  type="button"
                  buttonProps={{
                    size: 32,
                    borderColor: color.success,
                  }}
                  tooltip="Unarchive"
                  color={color.success}
                  size={20}
                  onClick={onClickUnarchive}
                  data-testid="identity-provider-unarchive-button"
                />
              </div>
            ) : null}
          </div>
        )}
      </SidePanelFooter>

      <VibeModal
        show={confirmArchive}
        type="confirm"
        title="Archive Identity Provider"
        text={`Are you sure you want to archive ${identityProvider.name}?`}
        confirmProps={{
          color: 'error',
        }}
        testIds={{
          root: 'archive-identity-provider-dialog',
          confirm: 'archive-identity-provider-confirm-button',
          cancel: 'archive-identity-provider-cancel-button',
        }}
        onConfirm={onConfirmArchive}
        onClose={onCloseArchive}
      />
    </SidePanelContainer>
  );
}

IdentityProviderDetails.propTypes = {
  className: PropTypes.string,
  style: PropTypes.object,
  identityProvider: PropTypes.oneOfType([
    PropTypes.object,
  ]),
  isNew: PropTypes.bool,
  onUpdate: PropTypes.func,
};

function mapStateToProps(state) {
  return {
    user: state.login.user,
  };
}

const mapDispatchToProps = {
  setPanel: GlobalActions.setPanel,
  queueToast: ToastActions.queueToast,
};

export default connect(mapStateToProps, mapDispatchToProps)(IdentityProviderDetails);
